import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

export interface StatTileProps {
  /** Ikona po lewej stronie liczby. */
  icon?: ReactNode;
  /** Glowna wartosc — liczba, czas, procent. */
  value: ReactNode;
  /** Podpis pod wartoscia. */
  label: ReactNode;
  /** Druga linia podpisu (np. „2 sety lub więcej”). */
  hint?: ReactNode;
  /** Czyni kafelek przyciskiem — np. otwarcie listy turniejów na telefonie. */
  onClick?: () => void;
  className?: string;
}

/**
 * Kafelek statystyki — „32 zawodników”, „12h 18m łączny czas gry”, „83% meczów”.
 * Uklad z sekcji „STATYSTYKI TURNIEJU” na ekranie klubowym. Z `onClick` staje
 * się przyciskiem (z efektem najechania), bez niego pozostaje biernym kafelkiem.
 */
export function StatTile({ icon, value, label, hint, onClick, className }: StatTileProps) {
  const content = (
    <>
      {icon && (
        <span className="flex size-9 shrink-0 items-center justify-center text-fg-muted [&_svg]:size-5">
          {icon}
        </span>
      )}
      <div className="flex min-w-0 flex-col">
        <span className="text-xl leading-tight font-extrabold tracking-[-0.02em] text-fg-strong">
          {value}
        </span>
        <span className="truncate text-xs text-fg-muted">{label}</span>
        {hint && <span className="truncate text-[0.6875rem] text-fg-subtle">{hint}</span>}
      </div>
    </>
  );

  const base = 'border-border bg-surface flex items-center gap-3 rounded-xl border p-3.5';

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(base, 'text-left transition-colors hover:bg-surface-hover', className)}
      >
        {content}
      </button>
    );
  }

  return <div className={cn(base, className)}>{content}</div>;
}
